import { useSyncExternalStore } from 'react';
import {
  canPushSettings,
  preferencesPatchFromSettings,
  settingsFromPreferences,
  settingsSyncState,
  VISIOUS_SCOPES,
  type SettingsSyncState,
} from '@visious/core';
import { fetchPreferences, pushPreferences, type PreferencesProblem } from '../api/preferences';
import { getPlatform } from './instance';
import { nowEpochSec, onSessionChange, refreshSession, type Session } from './session';
import {
  onSettingsWritten,
  readSettings,
  writeSettings,
} from './settings';
import { readSyncStamp, SYNC_STAMP_KEY, writeSyncStamp } from './syncStamp';

/**
 * §8's preference sync as a background concern — the instance is the source of truth, the local
 * stores are its cache (the 2026-08-01 revision that replaced the sign-in prompt).
 *
 * One module rather than logic on the settings page, because the model stopped being a page's:
 * a pull has to happen on every signed-in boot — whichever page it lands on — and a push has to
 * follow a write from anywhere, not only from a control the settings page drew. The page is left
 * with what is genuinely its own: rendering this module's snapshot.
 *
 * Two rules carry over from the prompt era, with new reasons:
 *
 * - **No push before a pull has landed** (`canPushSettings`). A push carries all eleven mapped
 *   fields, so pushing from a cache the account has since moved past would overwrite the account —
 *   the same overwrite the old `undecided` state existed to prevent, now bounded to one boot.
 * - **A failed push does not roll the local change back.** Local storage stays the thing the reader
 *   watched themselves change; the problem is shown and the next change (or pull) resolves it.
 *
 * A write that happens while the pull is still in flight is deliberately *not* queued: the account
 * wins the race by design, and the window is one request at boot.
 */

export interface SettingsSyncSnapshot {
  readonly state: SettingsSyncState;
  /** A pull or push in flight. */
  readonly busy: boolean;
  /** The last pull or push refusal, for the page to put into words (`describePreferencesProblem`). */
  readonly problem: PreferencesProblem | null;
  readonly lastSyncedEpochSec: number | null;
}

let signedIn = false;
let pulled = false;
let busy = false;
let problem: PreferencesProblem | null = null;

/** Guards the write listener against the pull's own `writeSettings` — that write must not push. */
let applyingRemote = false;

/** A pull that lands after a sign-out (or a newer pull) must not apply — the sequence says whose. */
let pullSeq = 0;

let pushTimer: ReturnType<typeof setTimeout> | null = null;
let pushing = false;
let pushAgain = false;

let snapshot: SettingsSyncSnapshot = {
  state: 'signed-out',
  busy: false,
  problem: null,
  lastSyncedEpochSec: readSyncStamp(),
};

const listeners = new Set<() => void>();

function emit(): void {
  snapshot = {
    state: settingsSyncState({ signedIn, pulled }),
    busy,
    problem,
    lastSyncedEpochSec: readSyncStamp(),
  };
  for (const listener of listeners) listener();
}

async function pull(): Promise<void> {
  const seq = ++pullSeq;
  busy = true;
  problem = null;
  emit();

  const boot = await getPlatform();
  if (seq !== pullSeq) return;
  if (!boot.ok) {
    busy = false;
    emit();
    return;
  }

  const result = await fetchPreferences(boot.platform, VISIOUS_SCOPES);
  if (seq !== pullSeq) return;
  busy = false;
  if (!result.ok) {
    problem = result.error;
    emit();
    return;
  }

  applyingRemote = true;
  try {
    writeSettings(settingsFromPreferences(result.value, readSettings()));
  } finally {
    applyingRemote = false;
  }
  pulled = true;
  writeSyncStamp(nowEpochSec());
  emit();
}

async function push(): Promise<void> {
  // Coalesced rather than parallel: two pushes racing are two read-merge-writes interleaving, and
  // the loser writes a merge over a document the winner already replaced.
  if (pushing) {
    pushAgain = true;
    return;
  }
  pushing = true;
  busy = true;
  problem = null;
  emit();

  const boot = await getPlatform();
  if (boot.ok && canPushSettings(settingsSyncState({ signedIn, pulled }))) {
    // The settings as they are *now*, not as they were when the write fired — coalesced changes
    // must send their final state, and the store's normalisers have had their say by now.
    const result = await pushPreferences(
      boot.platform,
      preferencesPatchFromSettings(readSettings()),
      VISIOUS_SCOPES,
    );
    if (!result.ok) problem = result.error;
    else writeSyncStamp(nowEpochSec());
  }

  pushing = false;
  busy = false;
  emit();
  if (pushAgain) {
    pushAgain = false;
    void push();
  }
}

/**
 * Debounced, because a push is two requests by design (read-merge-write) and one interaction can be
 * several writes — a number field commits every valid keystroke, so "40" typed over "4" is already
 * two. Long enough to coalesce a hand on a control, short enough that closing the tab right after a
 * change rarely loses it.
 */
const PUSH_DELAY_MS = 800;

function onLocalWrite(): void {
  if (applyingRemote) return;
  if (!canPushSettings(settingsSyncState({ signedIn, pulled }))) return;
  if (pushTimer !== null) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void push();
  }, PUSH_DELAY_MS);
}

function onSession(session: Session): void {
  if (!session.ready) return;
  const now = session.facts.signedIn;
  if (now === signedIn) return;
  signedIn = now;
  pulled = false;
  if (pushTimer !== null) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  if (signedIn) {
    void pull();
    return;
  }
  // Signing out ends the agreement: the stamp records that *this credential's* account and this
  // browser were in step, and the next sign-in — possibly another account — starts with its own pull.
  pullSeq += 1;
  busy = false;
  problem = null;
  writeSyncStamp(null);
  emit();
}

/**
 * Re-fetch the account's preferences and apply them — the manual arm of the boot pull, for a retry
 * after a failed one and for the Data tab's "clear cache" (the local copy of the account's settings
 * is a cache like any other). A no-op signed out: there is nothing to fetch from.
 */
export function refreshFromInstance(): void {
  if (!signedIn) return;
  void pull();
}

let initialized = false;

/** Called once from `main.tsx`. Subscribes to the session and to local settings writes. */
export function initSettingsSync(): void {
  if (initialized) return;
  initialized = true;
  onSessionChange(onSession);
  onSettingsWritten(onLocalWrite);
  /*
   * Another tab agreeing a sync used to reach this one through the settings store, the stamp having
   * shared its key. It has its own key now, so the watch is this module's — one listener for the
   * document rather than one per subscriber ([web-ui.md](../../../../docs/specs/web-ui.md)), and
   * `initSettingsSync` runs once, which is what makes that true here.
   */
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === null || event.key === SYNC_STAMP_KEY) emit();
  });
  /*
   * The first session read may already have happened (another subscriber won the race) or not have
   * started; both end in a publish, and the subscription above carries it to `onSession` with the
   * session as it stands at that moment.
   *
   * The **resolved value** of this read must not be passed on, and that is the whole reason this
   * line is not `.then(onSession)`: the read that runs while a sign-in page loads starts before the
   * grant is stored and resolves after it, so its value is a signed-out session arriving on top of a
   * signed-in one. `onSession` reads a transition out of it, bumps `pullSeq` and drops the pull the
   * sign-in had just started, which left a fresh session unsynced until the next page load.
   */
  void refreshSession();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot(): SettingsSyncSnapshot {
  return snapshot;
}

/** Where this browser stands with the account's preferences, re-read whenever that moves. */
export function useSettingsSync(): SettingsSyncSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
